import React, { useState, useEffect } from 'react';
import './DateOfBirthPicker.css';

interface DateOfBirthPickerProps {
  value?: string; // ISO date string (YYYY-MM-DD) or empty string
  onChange: (date: string | undefined) => void;
  maxDate?: Date; // Maximum allowed date (default: today)
  minDate?: Date; // Minimum allowed date (default: 100 years ago)
  error?: boolean;
  required?: boolean;
  label?: string;
  className?: string;
}

/**
 * Date of Birth Picker Component
 * 
 * Provides an easy-to-use date picker with separate dropdowns for Year, Month, and Day.
 * This is much more user-friendly than the native HTML5 date input, especially for
 * selecting birth dates that are many years in the past.
 * 
 * Features:
 * - Year dropdown (scrollable, default: 100 years back from today)
 * - Month dropdown (1-12)
 * - Day dropdown (automatically adjusts based on selected month/year)
 * - Handles leap years correctly
 * - Validates date ranges
 */
const DateOfBirthPicker: React.FC<DateOfBirthPickerProps> = ({
  value,
  onChange,
  maxDate = new Date(),
  minDate,
  error = false,
  required = false,
  label = 'Date of Birth',
  className = '',
}) => {
  const today = new Date();
  const defaultMinDate = new Date(today.getFullYear() - 100, 0, 1);
  const effectiveMinDate = minDate || defaultMinDate;

  // Parse the value into year, month, day
  const parseDate = (dateString?: string): { year: number; month: number; day: number } | null => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1, // 1-12
        day: date.getDate(),
      };
    } catch {
      return null;
    }
  };

  const initialDate = parseDate(value);
  const [selectedYear, setSelectedYear] = useState<number | ''>(initialDate?.year || '');
  const [selectedMonth, setSelectedMonth] = useState<number | ''>(initialDate?.month || '');
  const [selectedDay, setSelectedDay] = useState<number | ''>(initialDate?.day || '');

  // Update local state when value prop changes
  useEffect(() => {
    const parsed = parseDate(value);
    if (parsed) {
      setSelectedYear(parsed.year);
      setSelectedMonth(parsed.month);
      setSelectedDay(parsed.day);
    } else {
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
    }
  }, [value]);

  // Generate year options (from minDate to maxDate)
  const generateYears = (): number[] => {
    const years: number[] = [];
    const startYear = effectiveMinDate.getFullYear();
    const endYear = maxDate.getFullYear();
    for (let year = endYear; year >= startYear; year--) {
      years.push(year);
    }
    return years;
  };

  // Get number of days in a month (handles leap years)
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
  };

  // Generate day options based on selected month and year
  const generateDays = (): number[] => {
    if (!selectedYear || !selectedMonth) return [];
    const year = Number(selectedYear);
    const month = Number(selectedMonth);
    const daysInMonth = getDaysInMonth(year, month);
    const days: number[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  // Validate if the selected date is within range
  const isValidDate = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day &&
      date >= effectiveMinDate &&
      date <= maxDate
    );
  };

  // Handle year change
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = e.target.value === '' ? '' : Number(e.target.value);
    setSelectedYear(year);
    
    if (year && selectedMonth && selectedDay) {
      const day = Number(selectedDay);
      const month = Number(selectedMonth);
      
      // Adjust day if it's invalid for the new year (e.g., Feb 29 in non-leap year)
      const daysInMonth = getDaysInMonth(year, month);
      const newDay = day > daysInMonth ? daysInMonth : day;
      
      if (isValidDate(year, month, newDay)) {
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(newDay).padStart(2, '0')}`;
        onChange(dateStr);
        setSelectedDay(newDay);
      } else {
        onChange(undefined);
        setSelectedDay(newDay);
      }
    } else {
      onChange(undefined);
    }
  };

  // Handle month change
  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = e.target.value === '' ? '' : Number(e.target.value);
    setSelectedMonth(month);
    
    if (selectedYear && month && selectedDay) {
      const year = Number(selectedYear);
      const day = Number(selectedDay);
      
      // Adjust day if it's invalid for the new month (e.g., Feb 31)
      const daysInMonth = getDaysInMonth(year, month);
      const newDay = day > daysInMonth ? daysInMonth : day;
      
      if (isValidDate(year, month, newDay)) {
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(newDay).padStart(2, '0')}`;
        onChange(dateStr);
        setSelectedDay(newDay);
      } else {
        onChange(undefined);
        setSelectedDay(newDay);
      }
    } else {
      onChange(undefined);
    }
  };

  // Handle day change
  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const day = e.target.value === '' ? '' : Number(e.target.value);
    setSelectedDay(day);
    
    if (selectedYear && selectedMonth && day) {
      const year = Number(selectedYear);
      const month = Number(selectedMonth);
      
      if (isValidDate(year, month, day)) {
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        onChange(dateStr);
      } else {
        onChange(undefined);
      }
    } else {
      onChange(undefined);
    }
  };

  const years = generateYears();
  const days = generateDays();
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  return (
    <div className={`dob-picker-container ${className}`}>
      {label && (
        <label className="dob-picker-label">
          {label}
          {required && <span style={{ color: '#e30412' }}> *</span>}
        </label>
      )}
      <div className={`dob-picker-fields ${error ? 'dob-picker-error' : ''}`}>
        <select
          className="dob-picker-select dob-picker-year"
          value={selectedYear}
          onChange={handleYearChange}
          aria-label="Year"
        >
          <option value="">Year</option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        <select
          className="dob-picker-select dob-picker-month"
          value={selectedMonth}
          onChange={handleMonthChange}
          disabled={!selectedYear}
          aria-label="Month"
        >
          <option value="">Month</option>
          {months.map((month) => (
            <option key={month.value} value={month.value}>
              {month.label}
            </option>
          ))}
        </select>

        <select
          className="dob-picker-select dob-picker-day"
          value={selectedDay}
          onChange={handleDayChange}
          disabled={!selectedYear || !selectedMonth}
          aria-label="Day"
        >
          <option value="">Day</option>
          {days.map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default DateOfBirthPicker;

